// pkg125 — admin prop: Client Modules workspace renders this page with adminApi I/O (auto-save included); Send-to-Attorney hidden in admin mode (D-124e); Word download via admin endpoint — delivered 2026-07-27
// pkg102 — Case Info gains the "In this case, you are the:" selector, saved as
// pp_client_is ('plaintiff' | 'defendant'). The doc builder has always rendered
// the signature line as `Attorney for ${party}` from this key (with the Juvenile
// Petitioner/Respondent flip), but the form never asked, so the backend default
// made every plan read "Attorney for Plaintiff". Defaults to 'plaintiff' so all
// previously saved plans generate identical output — delivered 2026-07-18
// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { adminApi } from '../utils/adminApi'; // pkg125 — admin mode
import { useFirmSettings } from '../contexts/FirmSettingsContext';

// ─── FIELD COMPONENTS (match myTNdivorce.com exactly) ──────────────────────────
function Field({ label, help, required, children, error }) {
  return (
    <div className="form-group">
      <label>{label}{required && <span style={{ color: '#B85042' }}> *</span>}</label>
      {help && <div style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginBottom: 6 }}>{help}</div>}
      {children}
      {error && <div style={{ color: '#B85042', fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function sel(value, onChange, options, placeholder = '— Select —') {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map(o => {
        const v = typeof o === 'object' ? o.value : o;
        const l = typeof o === 'object' ? o.label : o;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

function radio(value, onChange, options) {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
      {options.map(opt => (
        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}>
          <input type="radio" checked={value === opt} onChange={() => onChange(opt)} />
          {opt}
        </label>
      ))}
    </div>
  );
}

// ─── HOLIDAY PAIR COMPONENT (exact match to myTNdivorce.com) ────────────────────
function HolidayPair({ label, motherKey, fatherKey, formData, onChange }) {
  const motherVal = formData[motherKey] || '';
  const fatherVal = formData[fatherKey] || '';
  const opposite = { ODD: 'EVEN', EVEN: 'ODD' };
  const isNA = motherVal === 'NA';

  function handleMotherChange(v) {
    onChange(motherKey, v);
    if (v === 'ODD' || v === 'EVEN') {
      onChange(fatherKey, opposite[v]);
    } else if (v === 'DAY_TO_DAY') {
      onChange(fatherKey, 'DAY_TO_DAY');
    } else if (v === 'NA') {
      onChange(fatherKey, 'NA');
    } else if (v === 'EVERY') {
      onChange(fatherKey, '');
    } else {
      onChange(fatherKey, '');
    }
  }

  const conflict = motherVal === fatherVal && (motherVal === 'ODD' || motherVal === 'EVEN');

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontWeight: 600, fontSize: 12, color: '#333', marginBottom: 4, display: 'block', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
      {isNA ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <select value={motherVal} onChange={e => handleMotherChange(e.target.value)}
            style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 3, fontSize: 14, fontFamily: 'Arial, sans-serif' }}>
            <option value="">— Select —</option>
            <option value="ODD">Mother — ODD years</option>
            <option value="EVEN">Mother — EVEN years</option>
            <option value="EVERY">Mother — EVERY year</option>
            <option value="DAY_TO_DAY">Day to day schedule applies</option>
            <option value="NA">Not applicable</option>
          </select>
          <div style={{ fontSize: 12, color: '#888', fontStyle: 'italic', fontFamily: 'Arial, sans-serif' }}>Not included in plan</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontFamily: 'Arial, sans-serif' }}>Mother gets:</div>
            <select value={motherVal} onChange={e => handleMotherChange(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 3, fontSize: 14, fontFamily: 'Arial, sans-serif' }}>
              <option value="">— Select —</option>
              <option value="ODD">ODD years</option>
              <option value="EVEN">EVEN years</option>
              <option value="EVERY">EVERY year</option>
              <option value="DAY_TO_DAY">Day to day schedule</option>
              <option value="NA">Not applicable</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontFamily: 'Arial, sans-serif' }}>Father gets:</div>
            <div style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 3, fontSize: 14, background: '#F5F1E8', color: '#555', fontFamily: 'Arial, sans-serif' }}>
              {fatherVal === 'DAY_TO_DAY' ? 'Day to day schedule' : fatherVal === 'NA' ? 'Not applicable' : fatherVal || '(auto-filled)'}
            </div>
          </div>
        </div>
      )}
      {conflict && <div style={{ color: '#B85042', fontSize: 12, marginTop: 4, fontFamily: 'Arial, sans-serif' }}>⚠️ Both parents cannot have the same year pattern for {label}.</div>}
    </div>
  );
}

// ─── CHRISTMAS SELECT (filters based on New Year's selection) ───────────────────
function ChristmasSelect({ value, onChange, formData }) {
  const newYearMother = formData.pp_holiday_new_year_mother || '';
  const ALL_OPTS = [
    { value: 'opt1', label: "Odd years: Father has school-out through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Jan 2 at 6pm. Rotates each year.", nyMother: 'ODD' },
    { value: 'opt2', label: "Even years: Father has school-out through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Jan 2 at 6pm. Rotates each year.", nyMother: 'EVEN' },
    { value: 'opt3', label: "Odd years: Father has Dec 24 at 2pm through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Dec 26 at 6pm. Rotates each year.", nyMother: 'ODD' },
    { value: 'opt4', label: "Even years: Father has Dec 24 at 2pm through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Dec 26 at 6pm. Rotates each year.", nyMother: 'EVEN' },
    { value: 'day_to_day', label: 'Day to day schedule applies', nyMother: null },
    { value: 'custom', label: 'Custom — enter my own language', nyMother: null },
  ];
  const opts = ALL_OPTS.filter(o =>
    o.nyMother === null || !newYearMother || newYearMother === 'EVERY' || o.nyMother === newYearMother
  );
  const hint = newYearMother === 'ODD' ? "Showing options consistent with Mother having New Year's in ODD years." :
    newYearMother === 'EVEN' ? "Showing options consistent with Mother having New Year's in EVEN years." : '';

  return (
    <div className="form-group">
      <label>Winter (Christmas) Break arrangement <span style={{ color: '#B85042' }}>*</span></label>
      {hint && <div style={{ fontSize: 12, color: '#0F1838', fontWeight: 600, marginBottom: 6, fontFamily: 'Arial, sans-serif' }}>{hint}</div>}
      <select value={value || ''} onChange={e => onChange('pp_winter_option', e.target.value)}>
        <option value="">— Select —</option>
        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ─── CHILD REPEATER ─────────────────────────────────────────────────────────────
function ChildRepeater({ children, onChange }) {
  const items = children || [];
  function add() { onChange([...items, { name: '', dob: '' }]); }
  function remove(i) { onChange(items.filter((_, idx) => idx !== i)); }
  function update(i, key, val) { onChange(items.map((c, idx) => idx === i ? { ...c, [key]: val } : c)); }

  return (
    <div>
      {items.map((c, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end', marginBottom: 10 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Child {i + 1} Full Name</label>
            <input type="text" value={c.name || ''} onChange={e => update(i, 'name', e.target.value)} placeholder="Full name" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Date of Birth</label>
            <input type="date" value={c.dob || ''} onChange={e => update(i, 'dob', e.target.value)} />
          </div>
          {items.length > 1 && (
            <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#B85042', cursor: 'pointer', fontSize: 20, padding: '8px 4px', lineHeight: 1 }}>×</button>
          )}
        </div>
      ))}
      <button className="btn btn-sm" onClick={add} style={{ marginTop: 4 }}>+ Add Child</button>
    </div>
  );
}

// ─── VACATION TEXT LOOKUPS (exact match to myTNdivorce.com) ────────────────────
const FALL_TEXT = {
  father_even: "Father shall parent the child(ren) in the even years and Mother shall parent the child(ren) in the odd years.",
  father_odd: "Father shall parent the child(ren) in the odd years and Mother shall parent the child(ren) in the even years.",
  day_to_day: "The day to day schedule shall apply.",
};
const SPRING_TEXT = {
  father_even: "Father shall parent the child(ren) in the even years and Mother shall parent the child(ren) in the odd years.",
  father_odd: "Father shall parent the child(ren) in the odd years and Mother shall parent the child(ren) in the even years.",
  day_to_day: "The day to day schedule shall apply.",
};
const CHRISTMAS_TEXT = {
  opt1: "The day to day schedule shall apply except: In the odd years, the Father shall parent the child(ren) from the day and time school lets out until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until January 2nd at 6pm. This schedule shall rotate each year.",
  opt2: "The day to day schedule shall apply except: In the even years, the Father shall parent the child(ren) from the day and time school lets out until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until January 2nd at 6pm. This schedule shall rotate each year.",
  opt3: "The day to day schedule shall apply except: In the odd years, the Father shall parent the child(ren) from December 24th at 2pm until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until December 26th at 6pm. This schedule shall rotate each year.",
  opt4: "The day to day schedule shall apply except: In the even years, the Father shall parent the child(ren) from December 24th at 2pm until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until December 26th at 6pm. This schedule shall rotate each year.",
  day_to_day: "The day to day schedule shall apply.",
};
const SUMMER_TEXT = {
  day_to_day: "The day to day schedule shall apply.",
  week_on_off: "The parents shall parent the child(ren) alternating week on and week off.",
  two_weeks: "Each parent shall have two non-consecutive weeks of parenting time upon 30 days notice to the other parent. In the even years, Father shall choose his two weeks by April 1st each year and then Mother shall choose her two weeks by April 15. Mother shall choose first in the odd years.",
};

// ─── SECTIONS ────────────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'case_info', title: 'Case Information' },
  { id: 'children', title: 'Children' },
  { id: 'schedule', title: 'Parenting Plan — Residential Schedule' },
  { id: 'holidays', title: 'Parenting Plan — Holiday Schedule' },
  { id: 'vacations', title: 'Parenting Plan — School Breaks & Transportation' },
  { id: 'decisions', title: 'Parenting Plan — Decision-Making' },
  { id: 'taxes', title: 'Parenting Plan — Tax Exemptions' },
  { id: 'special', title: 'Parenting Plan — Special Provisions' },
  { id: 'education', title: 'Parenting Plan — Parent Education Class' },
];

const EMPTY = {
  // Case
  plaintiff_name: '', defendant_name: '',
  plaintiff_role: 'Wife', defendant_role: 'Husband',
  plaintiff_gender: 'Female (she/her)', defendant_gender: 'Male (he/him)',
  case_county: 'Hamilton County, Tennessee', case_court: 'Circuit', case_number: '',
  pp_client_is: 'plaintiff', // pkg102 — which party the client is; drives "Attorney for ___"
  pp_plan_type: 'agreed',
  // Children
  children: [{ name: '', dob: '' }],
  // Schedule
  pp_prp: '', pp_schedule_type: '', pp_weeknight: '',
  pp_exchange_day: '', pp_exchange_time: '',
  pp_joint_exchange_day: '', pp_joint_exchange_time: '',
  pp_custom_schedule: '',
  pp_mother_days: '', pp_father_days: '',
  // Holidays
  pp_holiday_new_year_mother: '', pp_holiday_new_year_father: '',
  pp_holiday_mlk_mother: '', pp_holiday_mlk_father: '',
  pp_holiday_presidents_mother: '', pp_holiday_presidents_father: '',
  pp_holiday_easter_mother: '', pp_holiday_easter_father: '',
  pp_holiday_passover_mother: '', pp_holiday_passover_father: '',
  pp_holiday_memorial_mother: '', pp_holiday_memorial_father: '',
  pp_holiday_july4_mother: '', pp_holiday_july4_father: '',
  pp_holiday_labor_mother: '', pp_holiday_labor_father: '',
  pp_holiday_halloween_mother: '', pp_holiday_halloween_father: '',
  pp_holiday_thanksgiving_mother: '', pp_holiday_thanksgiving_father: '',
  pp_holiday_time_exception: '',
  // Vacations
  pp_fall_option: '', pp_fall_vacation: '',
  pp_spring_option: '', pp_spring_vacation: '',
  pp_winter_option: '', pp_winter_vacation: '',
  pp_summer_option: '', pp_summer_vacation: '',
  pp_summer_notice: 'No', pp_summer_notice_days: '30',
  pp_exchange_location: 'As agreed by the parties',
  pp_transportation_costs: 'na', pp_transportation_other: '',
  // Decisions
  pp_decision_education: 'joint', pp_decision_health: 'joint',
  pp_decision_religion: 'joint', pp_decision_extracurricular: 'joint',
  // Optional 5th custom decision-making category. If the label is left blank,
  // the document renders an empty underline with no boxes checked. If the user
  // enters a label, they can also pick who makes that decision.
  pp_decision_custom_label: '', pp_decision_custom_who: '',
  // Taxes
  pp_mother_exemption: '', pp_father_exemption: '',
  pp_exemption_type: 'alternate',
  // Special
  pp_special_provisions: '', pp_college: '',
  // Education
  pp_parent_education: 'neither',
};

// pkg125 — `admin` prop ({ clientId }): rendered by the Client Modules
// workspace with adminApi I/O (including the once-a-minute auto-save);
// Send-to-Attorney hidden (D-124e); Word download via the admin endpoint.
export default function ParentingPlanPage({ admin = null }) {
  const { firm } = useFirmSettings();
  const io = admin ? {
    get:  () => adminApi.getClientParentingPlan(admin.clientId),
    save: (data) => adminApi.saveClientParentingPlan(admin.clientId, data),
  } : {
    get:  () => api.getParentingPlan(),
    save: (data) => api.saveParentingPlan(data),
  };
  const [sectionIdx, setSectionIdx] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [showSendOptions, setShowSendOptions] = useState(false);
  const autoSaveRef = useRef(null);

  useEffect(() => {
    io.get().then(rec => {
      if (rec?.data) setForm({ ...EMPTY, ...rec.data });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    autoSaveRef.current = setInterval(() => {
      io.save(resolveVacations(form)).catch(() => {});
    }, 60000);
    return () => clearInterval(autoSaveRef.current);
  }, [form]);

  function set(key, val) {
    setForm(f => {
      const next = { ...f, [key]: val };
      // Joint PRP: lock days to 182.5
      if (key === 'pp_prp' && val === 'joint') {
        next.pp_mother_days = '182.5';
        next.pp_father_days = '182.5';
      }
      if (key === 'pp_prp' && val !== 'joint') {
        if (f.pp_mother_days === '182.5') next.pp_mother_days = '';
        if (f.pp_father_days === '182.5') next.pp_father_days = '';
      }
      return next;
    });
  }

  function resolveVacations(f) {
    const resolved = { ...f };
    if (f.pp_fall_option && f.pp_fall_option !== 'custom')
      resolved.pp_fall_vacation = FALL_TEXT[f.pp_fall_option] || '';
    if (f.pp_spring_option && f.pp_spring_option !== 'custom')
      resolved.pp_spring_vacation = SPRING_TEXT[f.pp_spring_option] || '';
    if (f.pp_winter_option && f.pp_winter_option !== 'custom')
      resolved.pp_winter_vacation = CHRISTMAS_TEXT[f.pp_winter_option] || '';
    if (f.pp_summer_option && f.pp_summer_option !== 'custom')
      resolved.pp_summer_vacation = SUMMER_TEXT[f.pp_summer_option] || '';
    return resolved;
  }

  async function handleSave() {
    setSaving(true); setStatus(''); setError('');
    try {
      await io.save(resolveVacations(form));
      setStatus('Progress saved.');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleSend() {
    setSending(true); setStatus(''); setError('');
    try {
      const email = recipientEmail.trim() || undefined;
      await api.sendParentingPlan(resolveVacations(form), email);
      setStatus(`Parenting plan sent to ${email || firm.client_contact_email || 'the firm'}.`);
      setShowSendOptions(false);
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  }

  function handleDownload() {
    // pkg125 — admin mode downloads through the admin endpoint (adminApi
    // carries the admin JWT); client path unchanged.
    if (admin) {
      adminApi.downloadParentingPlan(admin.clientId)
        .then(blob => {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'Parenting_Plan.docx';
          a.click();
        })
        .catch(err => setError(err.message));
      return;
    }
    const token = localStorage.getItem('ryanlaw_token');
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/parenting-plan/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'Parenting_Plan.docx';
        a.click();
      });
  }

  const isJoint = form.pp_prp === 'joint';
  const section = SECTIONS[sectionIdx];

  function nextSection() {
    if (sectionIdx < SECTIONS.length - 1) { setSectionIdx(i => i + 1); window.scrollTo(0, 0); }
  }
  function prevSection() {
    if (sectionIdx > 0) { setSectionIdx(i => i - 1); window.scrollTo(0, 0); }
  }

  if (loading) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;

  return (
    <div>
      <h1 className="page-title">Parenting Plan</h1>
      <p className="page-subtitle">
        Walk through your parenting preferences. Answers are saved automatically every minute and can be edited anytime.
        Child support is not included here — your attorney will handle that separately.
      </p>

      {/* Section progress */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {SECTIONS.map((s, i) => (
          <button key={i} onClick={() => { setSectionIdx(i); window.scrollTo(0, 0); }}
            style={{
              width: 28, height: 28, borderRadius: '50%', border: '2px solid',
              borderColor: i === sectionIdx ? '#0F1838' : i < sectionIdx ? '#0F1838' : '#ccc',
              background: i === sectionIdx ? '#0F1838' : i < sectionIdx ? '#0F1838' : 'white',
              color: i <= sectionIdx ? 'white' : '#888',
              fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            {i < sectionIdx ? '✓' : i + 1}
          </button>
        ))}
      </div>

      <div style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#888', marginBottom: 16 }}>
        Section {sectionIdx + 1} of {SECTIONS.length} — {section.title}
      </div>

      {status && <div className="alert alert-success">{status}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h2 className="card-title">{section.title}</h2>

        {/* ── CASE INFO ── */}
        {section.id === 'case_info' && (
          <>
            <div className="form-row">
              <Field label="Plaintiff / Petitioner Full Name" required>
                <input type="text" value={form.plaintiff_name} onChange={e => set('plaintiff_name', e.target.value)} placeholder="Full legal name" />
              </Field>
              <Field label="Defendant / Respondent Full Name" required>
                <input type="text" value={form.defendant_name} onChange={e => set('defendant_name', e.target.value)} placeholder="Full legal name" />
              </Field>
            </div>
            {/* pkg102 — drives the "Attorney for ___" line in the signature block */}
            <Field label="In this case, you are the:" required
              help="Choose which side of the case you are on. This sets the attorney signature line in the finished document.">
              {sel(form.pp_client_is, v => set('pp_client_is', v), [
                { value: 'plaintiff', label: 'Plaintiff / Petitioner' },
                { value: 'defendant', label: 'Defendant / Respondent' },
              ])}
            </Field>
            <div className="form-row">
              <Field label="Plaintiff is referred to as">
                {sel(form.plaintiff_role, v => set('plaintiff_role', v), ['Wife', 'Husband', 'Spouse'])}
              </Field>
              <Field label="Defendant is referred to as">
                {sel(form.defendant_role, v => set('defendant_role', v), ['Husband', 'Wife', 'Spouse'])}
              </Field>
            </div>
            <div className="form-row">
              <Field label="Plaintiff gender (for pronouns)">
                {sel(form.plaintiff_gender, v => set('plaintiff_gender', v), ['Female (she/her)', 'Male (he/him)', 'Non-binary (they/them)'])}
              </Field>
              <Field label="Defendant gender (for pronouns)">
                {sel(form.defendant_gender, v => set('defendant_gender', v), ['Male (he/him)', 'Female (she/her)', 'Non-binary (they/them)'])}
              </Field>
            </div>
            <div className="form-row">
              <Field label="Court (County and State)" required>
                <input type="text" value={form.case_county} onChange={e => set('case_county', e.target.value)} placeholder="e.g. Hamilton County, Tennessee" />
              </Field>
              <Field label="Court type">
                {sel(form.case_court, v => set('case_court', v), [
                  { value: 'Circuit', label: 'Circuit Court' },
                  { value: 'Chancery', label: 'Chancery Court' },
                  { value: 'Juvenile', label: 'Juvenile Court' },
                ])}
              </Field>
            </div>
            <Field label="Case Number (if known)">
              <input type="text" value={form.case_number} onChange={e => set('case_number', e.target.value)} placeholder="Leave blank if unknown" />
            </Field>
            <Field label="Plan type" required
              help='"Agreed" = both parents agree to this plan. "Proposed" = you are submitting this plan and the other parent has not (yet) agreed to it.'>
              {sel(form.pp_plan_type, v => set('pp_plan_type', v), [
                { value: 'agreed', label: 'Agreed — both parents agree to this plan' },
                { value: 'proposed', label: 'Proposed — submitted by one parent only' },
              ])}
            </Field>
          </>
        )}

        {/* ── CHILDREN ── */}
        {section.id === 'children' && (
          <>
            <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 16 }}>
              List all minor children born of or adopted during the marriage.
            </p>
            <ChildRepeater children={form.children} onChange={v => set('children', v)} />
          </>
        )}

        {/* ── SCHEDULE ── */}
        {section.id === 'schedule' && (
          <>
            <Field label="Who is the Primary Residential Parent (PRP)?" required
              help="The primary residential parent is the parent with whom the child lives the majority of the time.">
              {sel(form.pp_prp, v => set('pp_prp', v), [
                { value: 'plaintiff', label: 'Plaintiff (filing spouse) — Plaintiff is PRP' },
                { value: 'defendant', label: 'Defendant (other spouse) — Defendant is PRP' },
                { value: 'joint', label: 'Joint — equal time (182.5 days each)' },
              ])}
            </Field>

            {isJoint && (
              <div className="alert alert-info" style={{ marginBottom: 16 }}>
                <strong>Joint Primary Residential Parents — by agreement only.</strong> Both parents must agree, and parenting time must be exactly equal: <strong>182.5 days each per year.</strong>
              </div>
            )}

            {!isJoint && (
              <>
                <div className="form-row">
                  <Field label="Total days per year with Mother" required
                    help="Mother + Father days must equal 365.">
                    <input type="number" step="0.5" min="0" max="365" value={form.pp_mother_days}
                      onChange={e => set('pp_mother_days', e.target.value)} placeholder="e.g. 245" />
                  </Field>
                  <Field label="Total days per year with Father" required>
                    <input type="number" step="0.5" min="0" max="365" value={form.pp_father_days}
                      onChange={e => set('pp_father_days', e.target.value)} placeholder="e.g. 120" />
                  </Field>
                </div>
                {form.pp_mother_days && form.pp_father_days &&
                  Math.abs(parseFloat(form.pp_mother_days) + parseFloat(form.pp_father_days) - 365) > 0.5 && (
                    <div style={{ color: '#B85042', fontSize: 12, marginBottom: 12, fontFamily: 'Arial, sans-serif' }}>
                      ⚠️ Mother + Father days total {parseFloat(form.pp_mother_days) + parseFloat(form.pp_father_days)}. They must equal 365.
                    </div>
                  )}
              </>
            )}

            {!isJoint && (
              <Field label="What type of parenting schedule will you use?" required>
                {sel(form.pp_schedule_type, v => set('pp_schedule_type', v), [
                  { value: 'standard', label: 'PRP has primary custody; ARP has every other weekend + one weeknight' },
                  { value: 'week_on_week_off', label: 'Week On / Week Off — equal 50/50 alternating weeks' },
                  { value: 'custom', label: 'Custom — describe your own schedule below' },
                ])}
              </Field>
            )}

            {!isJoint && form.pp_schedule_type === 'standard' && (
              <Field label="Which weeknight does the ARP have?">
                {sel(form.pp_weeknight, v => set('pp_weeknight', v), ['Monday', 'Tuesday', 'Wednesday', 'Thursday'])}
              </Field>
            )}

            {!isJoint && form.pp_schedule_type === 'week_on_week_off' && (
              <div className="form-row">
                <Field label="Exchange day of week">
                  {sel(form.pp_exchange_day, v => set('pp_exchange_day', v), ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'])}
                </Field>
                <Field label="Exchange time">
                  <input type="text" value={form.pp_exchange_time} onChange={e => set('pp_exchange_time', e.target.value)} placeholder="6:00 p.m." />
                </Field>
              </div>
            )}

            {isJoint && (
              <div className="form-row">
                <Field label="Exchange day for week-on/week-off schedule">
                  {sel(form.pp_joint_exchange_day, v => set('pp_joint_exchange_day', v), ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'])}
                </Field>
                <Field label="Exchange time">
                  <input type="text" value={form.pp_joint_exchange_time} onChange={e => set('pp_joint_exchange_time', e.target.value)} placeholder="6:00 p.m." />
                </Field>
              </div>
            )}

            {!isJoint && form.pp_schedule_type === 'custom' && (
              <Field label="Describe your parenting schedule in full">
                <textarea value={form.pp_custom_schedule} onChange={e => set('pp_custom_schedule', e.target.value)}
                  placeholder="e.g. Children reside primarily with Mother. Father has parenting time on the 1st and 3rd weekends of each month from Friday at 6:00 p.m. to Sunday at 6:00 p.m..."
                  style={{ minHeight: 100 }} />
              </Field>
            )}
          </>
        )}

        {/* ── HOLIDAYS ── */}
        {section.id === 'holidays' && (
          <>
            <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 16 }}>
              For each holiday, select which years Mother gets it. Father automatically gets the opposite. Select EVERY if one parent always gets it. Select "Not applicable" to exclude the holiday from the plan entirely.
            </p>
            {[
              ["New Year's Day", 'pp_holiday_new_year'],
              ['Martin Luther King Day', 'pp_holiday_mlk'],
              ["Presidents' Day", 'pp_holiday_presidents'],
              ['Easter Day', 'pp_holiday_easter'],
              ['Passover Day', 'pp_holiday_passover'],
              ['Memorial Day', 'pp_holiday_memorial'],
              ['July 4th', 'pp_holiday_july4'],
              ['Labor Day', 'pp_holiday_labor'],
              ['Halloween', 'pp_holiday_halloween'],
              ['Thanksgiving Day & Friday', 'pp_holiday_thanksgiving'],
            ].map(([label, mk]) => (
              <HolidayPair key={mk} label={label}
                motherKey={mk + '_mother'} fatherKey={mk + '_father'}
                formData={form} onChange={(key, val) => set(key, val)} />
            ))}
            <Field label="Holiday time exception (if any)">
              <input type="text" value={form.pp_holiday_time_exception}
                onChange={e => set('pp_holiday_time_exception', e.target.value)}
                placeholder="e.g. Thanksgiving ends at 8:00 p.m. Sunday" />
            </Field>
          </>
        )}

        {/* ── VACATIONS ── */}
        {section.id === 'vacations' && (
          <>
            <Field label="Fall Break arrangement" required>
              {sel(form.pp_fall_option, v => set('pp_fall_option', v), [
                { value: 'father_even', label: "Father — even years / Mother — odd years" },
                { value: 'father_odd', label: "Father — odd years / Mother — even years" },
                { value: 'day_to_day', label: 'Day to day schedule applies' },
                { value: 'custom', label: 'Custom — enter my own language' },
              ])}
            </Field>
            {form.pp_fall_option === 'custom' && (
              <Field label="Custom fall break language" required>
                <textarea value={form.pp_fall_vacation} onChange={e => set('pp_fall_vacation', e.target.value)}
                  placeholder="Enter the fall break parenting arrangement..." />
              </Field>
            )}

            <Field label="Spring Break arrangement" required>
              {sel(form.pp_spring_option, v => set('pp_spring_option', v), [
                { value: 'father_even', label: "Father — even years / Mother — odd years" },
                { value: 'father_odd', label: "Father — odd years / Mother — even years" },
                { value: 'day_to_day', label: 'Day to day schedule applies' },
                { value: 'custom', label: 'Custom — enter my own language' },
              ])}
            </Field>
            {form.pp_spring_option === 'custom' && (
              <Field label="Custom spring break language" required>
                <textarea value={form.pp_spring_vacation} onChange={e => set('pp_spring_vacation', e.target.value)}
                  placeholder="Enter the spring break parenting arrangement..." />
              </Field>
            )}

            <ChristmasSelect value={form.pp_winter_option} onChange={set} formData={form} />
            {form.pp_winter_option === 'custom' && (
              <Field label="Custom Christmas break language" required>
                <textarea value={form.pp_winter_vacation} onChange={e => set('pp_winter_vacation', e.target.value)}
                  placeholder="Enter the Christmas break parenting arrangement..." />
              </Field>
            )}

            <Field label="Summer Vacation arrangement" required>
              {sel(form.pp_summer_option, v => set('pp_summer_option', v), [
                { value: 'day_to_day', label: 'Day to day schedule applies' },
                { value: 'week_on_off', label: 'Parents alternate week on / week off' },
                { value: 'two_weeks', label: "Each parent has two non-consecutive weeks upon 30 days notice (Father chooses first in even years by April 1; Mother chooses first in odd years)" },
                { value: 'custom', label: 'Custom — enter my own language' },
              ])}
            </Field>
            {form.pp_summer_option === 'custom' && (
              <Field label="Custom summer language" required>
                <textarea value={form.pp_summer_vacation} onChange={e => set('pp_summer_vacation', e.target.value)}
                  placeholder="Enter the summer parenting arrangement..." />
              </Field>
            )}

            <Field label="Is written notice required for summer vacation?" required>
              {radio(form.pp_summer_notice, v => set('pp_summer_notice', v), ['Yes', 'No'])}
            </Field>
            {form.pp_summer_notice === 'Yes' && (
              <Field label="Number of days notice required">
                <input type="text" value={form.pp_summer_notice_days} onChange={e => set('pp_summer_notice_days', e.target.value)} placeholder="30" />
              </Field>
            )}

            <div style={{ borderTop: '1px solid #ddd', margin: '20px 0' }} />
            <div className="section-head" style={{ marginTop: 0 }}>Transportation</div>

            <Field label="Exchange location for parenting time" required>
              <input type="text" value={form.pp_exchange_location} onChange={e => set('pp_exchange_location', e.target.value)}
                placeholder="As agreed by the parties" />
            </Field>
            <Field label="Who pays long-distance transportation costs?" required>
              {sel(form.pp_transportation_costs, v => set('pp_transportation_costs', v), [
                { value: 'na', label: 'Not applicable' },
                { value: 'mother', label: 'Mother' },
                { value: 'father', label: 'Father' },
                { value: 'both', label: 'Both equally' },
              ])}
            </Field>
            <Field label="Other transportation arrangements (optional)">
              <input type="text" value={form.pp_transportation_other} onChange={e => set('pp_transportation_other', e.target.value)} placeholder="Optional" />
            </Field>
          </>
        )}

        {/* ── DECISIONS ── */}
        {section.id === 'decisions' && (
          <>
            <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 16 }}>
              Major decisions include education, medical care, religious upbringing, and extracurricular activities. Defaults to joint decision-making.
            </p>
            {[
              ['Educational decisions shall be made by', 'pp_decision_education'],
              ['Non-emergency health care decisions shall be made by', 'pp_decision_health'],
              ['Religious upbringing decisions shall be made by', 'pp_decision_religion'],
              ['Extracurricular activity decisions shall be made by', 'pp_decision_extracurricular'],
            ].map(([label, key]) => (
              <Field key={key} label={label} required>
                {sel(form[key], v => set(key, v), [
                  { value: 'joint', label: 'Both jointly' },
                  { value: 'mother', label: 'Mother' },
                  { value: 'father', label: 'Father' },
                ])}
              </Field>
            ))}
            <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginTop: 24, marginBottom: 8 }}>
              <strong>Optional:</strong> Add a 5th custom decision-making category (e.g. travel, school choice, mental health treatment). If you leave the label blank, the document will show a blank underline with no boxes checked.
            </p>
            <Field label="Custom decision-making category (optional)">
              <input type="text" value={form.pp_decision_custom_label}
                onChange={e => set('pp_decision_custom_label', e.target.value)}
                placeholder="e.g. Travel decisions" />
            </Field>
            {form.pp_decision_custom_label && form.pp_decision_custom_label.trim() && (
              <Field label="Who makes this decision?">
                {sel(form.pp_decision_custom_who, v => set('pp_decision_custom_who', v), [
                  { value: 'joint', label: 'Both jointly' },
                  { value: 'mother', label: 'Mother' },
                  { value: 'father', label: 'Father' },
                ], '— No boxes checked —')}
              </Field>
            )}
          </>
        )}

        {/* ── TAXES ── */}
        {section.id === 'taxes' && (
          <>
            <Field label="Tax exemption — children claimed by Mother">
              <input type="text" value={form.pp_mother_exemption} onChange={e => set('pp_mother_exemption', e.target.value)}
                placeholder="e.g. All children / Child 1 in odd years / None" />
            </Field>
            <Field label="Tax exemption — children claimed by Father">
              <input type="text" value={form.pp_father_exemption} onChange={e => set('pp_father_exemption', e.target.value)}
                placeholder="e.g. Child 2 / All children in even years / None" />
            </Field>
            <Field label="How are exemptions claimed?" required>
              {sel(form.pp_exemption_type, v => set('pp_exemption_type', v), [
                { value: 'alternate', label: 'Alternating years' },
                { value: 'every', label: 'Every year as specified above' },
                { value: 'other', label: 'Other arrangement' },
              ])}
            </Field>
          </>
        )}

        {/* ── SPECIAL ── */}
        {section.id === 'special' && (
          <>
            <Field label="Special provisions — any other parenting provisions (optional)">
              <textarea value={form.pp_special_provisions} onChange={e => set('pp_special_provisions', e.target.value)}
                placeholder="e.g. Neither parent shall allow overnight guests of the opposite sex who are not family members while the children are present."
                style={{ minHeight: 100 }} />
            </Field>
            <Field label="Post-secondary education provisions (optional)">
              <textarea value={form.pp_college} onChange={e => set('pp_college', e.target.value)}
                placeholder="e.g. The parties agree to equally divide the cost of the children's tuition, room and board at an in-state public university."
                style={{ minHeight: 80 }} />
            </Field>
          </>
        )}

        {/* ── PARENT EDUCATION ── */}
        {section.id === 'education' && (
          <>
            <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 16 }}>
              Tennessee courts require both parents to complete a parent education seminar within 60 days of filing.
            </p>
            <Field label="Parent education class status" required>
              {sel(form.pp_parent_education, v => set('pp_parent_education', v), [
                { value: 'both', label: 'Completed by both parents' },
                { value: 'mother', label: 'Completed by Mother only' },
                { value: 'father', label: 'Completed by Father only' },
                { value: 'neither', label: 'Not yet completed by either parent' },
              ])}
            </Field>
          </>
        )}

        {/* Nav */}
        <div className="btn-row" style={{ marginTop: 24 }}>
          {sectionIdx > 0 && (
            <button className="btn" onClick={prevSection}>← Back</button>
          )}
          {sectionIdx < SECTIONS.length - 1 ? (
            <button className="btn btn-primary" onClick={nextSection}>Next →</button>
          ) : (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner" />Saving...</> : 'Save All'}
            </button>
          )}
        </div>
      </div>

      {/* Actions always visible at bottom */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title" style={{ fontSize: 14, marginBottom: 16 }}>Actions</div>
        <div className="btn-row">
          <button className="btn" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner" />Saving...</> : 'Save Progress'}
          </button>
          {!admin && (
            <button className="btn btn-primary" onClick={() => setShowSendOptions(!showSendOptions)}>
              Send to Attorney
            </button>
          )}
          <button className="btn" onClick={handleDownload}>
            Download Word Doc
          </button>
        </div>

        {!admin && showSendOptions && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #ddd' }}>
            <div className="form-group">
              <label>Recipient Email (optional — leave blank to send to your attorney)</label>
              <input type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)}
                placeholder={firm.client_contact_email ? `Leave blank to send to ${firm.client_contact_email}` : 'Recipient email'} />
            </div>
            <div className="btn-row">
              <button className="btn btn-success" onClick={handleSend} disabled={sending}>
                {sending ? <><span className="spinner" />Sending...</> : `Send to ${recipientEmail || firm.client_contact_email || 'the firm'}`}
              </button>
              <button className="btn" onClick={() => setShowSendOptions(false)}>Cancel</button>
            </div>
          </div>
        )}

        <p style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 12 }}>
          Note: Child support provisions are not included here and will be handled separately by your attorney.
          Your progress is auto-saved every minute.
        </p>
      </div>
    </div>
  );
}
